import * as React from "react";
import {cx} from "emotion";

import "./for-select-editions-step.scss";

import {ReactGASCEnrollment} from "@Common/libs/ga/xd-react-ga";
import {to_getFriendlyName}  from "@Common/logic/template-options/template-options-get-friendly-name";
import {cs}                  from "@Common/react/chain-services";
import {consumeContext}      from "@Common/react/context";
import {fragments}           from "@Common/react/fragments";
import {keyed}               from "@Common/react/keyed";
import {scnv}                from "@Common/react/state-change-nested-value";
import {UseState}            from "@Common/react/use-state";
import {DomSizeObserver}     from "@Common/ui-components/dom-size-observer";
import {Checkbox}            from "@Common/ui-components/heavy/common/design2/checkbox/checkbox";
import {setCurrentToSaved}   from "@Common/ui-components/heavy/item-options/modules/set-current-to-saved";
import {plural}              from "@Common/ui-components/language/plural";
import {cnv}                 from "@Common/utils/arr-path";
import {sort, unique}        from "@Common/utils/collections";
import {chain}               from "@Common/utils/fs";
import {equalDeep}           from "@Common/utils/objects";

import {groupEnrollmentEditors} from "../../../../common/group-enrollment-editors";
import {checkMidwaySelect}      from "../common/check-midway-select";
import {isEnrollable}           from "../common/is-enrollable";

import {AutoEnroll}         from "./auto-enroll/auto-enroll";
import {AutoRenew}          from "./auto-renew/auto-renew";
import {EditionCard1}       from "./edition-card-1";
import {PricingTableModal3} from "./pricing-table-modal-3/pricing-table-modal-3";
import {TopSelect}          from "./top-select";

export const ForSelectEditionsStep = (
    {
        calValue,
        selectedEditions,
        seriesList,
        isUnselectable,
        initEnrollment,
        program,
        next,
    }
) => cs(
    consumeContext("session"),
    consumeContext("apis"),

    ["isSelected", (_, next) => next((editionId) => calValue.some((ue) => ue.editionId === editionId))],
    ["deselect", (_, next) => next((ids) => {

        for (const id of ids) {
            ReactGASCEnrollment({
                eventName: "deselect_edition",
                idProgram: program.idProgram,
                data: {
                    editionId: id,
                },
            });
        }

        scnv(selectedEditions, ["current"], (_) => calValue.filter((ue) => !ids.includes(ue.editionId)));
    })],
    ["select", (_, next) => next((ids) => {

        for (const id of ids) {
            ReactGASCEnrollment({
                eventName: "select_edition",
                idProgram: program.idProgram,
                data: {
                    editionId: id,
                },
            });
        }

        scnv(selectedEditions, ["current"], (_) => [
            ...calValue,
            ...ids.map((editionId) => ({
                editionId,
                // comment : Will be set in personalization step
                // templateId: this will be calculated
            })),
        ]);
    })],


    ["autoEnroll", ({}, next) => AutoEnroll({
        initEnrollment,
        program,
        next,
    })],
    ["autoRenew", ({autoEnroll}, next) => AutoRenew({
        initEnrollment,
        program,
        allowSeries: (seriesId) => !autoEnroll.isSelected(seriesId),
        permanentlyDisableSeries: autoEnroll.isForceSelected,
        next,
    })],
    ({session, isSelected, deselect, select, autoEnroll, autoRenew}) => next({
        enrollmentEditor: groupEnrollmentEditors([
            (() => {
                const changed = !equalDeep(calValue, selectedEditions.value.saved);
                return {
                    valid: !!calValue?.length,
                    $name: "Schedules",
                    prepareSave: (enrollment) => ({
                        newValue: cnv(enrollment, ["editionsPreferences"], (_) => calValue?.map((s) => ({
                            ...s,
                            idAgent: session.value.agentId,
                            idProgram: program.idProgram,
                            templateId: program.programPreferences.editions.find((s1) => s1.editionId === s.editionId).templateId,
                        }))),
                        onSaved: () => setCurrentToSaved(selectedEditions),
                    }),
                    changed,
                    needUrgentSaving: (
                        changed &&
                        selectedEditions.value.current?.some((e) => (
                            e.comment !== selectedEditions.value.saved?.find((e1) => e1.editionId === e.editionId)?.comment
                        ))
                    ),
                };
            })(),
            autoEnroll.enrollmentEditor,
            autoRenew.enrollmentEditor,
        ]),
        topControl: cs(
            () => TopSelect({
                seriesList,
                isSelected,
                deselect,
                select,
                isUnselectable,
            }),
        ),
        render: ({getStyleTemplate, templateOptionsList}) => cs(
            ({}, next) => (
                <div className="for-select-editions-step-1e8">
                    {next()}
                </div>
            ),
            ({}, next) => !seriesList.length ? (
                <div className="no-data">
                    {"No editions available"}
                </div>
            ) : (
                next()
            ),
            consumeContext("apis"),
            ["editions1", ({apis}, next) => (
                seriesList.map(({series, editions}) => cs(
                    keyed(series.id),
                    ["selected", (_, next) => next(checkMidwaySelect(editions, (e) => isSelected(e.editionId)))],
                    ({selected}) => (
                        <div {...{
                            className: "series",
                            key: series.id,
                            "xd-qa-type": "card",
                            "xd-qa-name": series.name + " Series",
                        }}>
                            <div {...{
                                className: "header",
                            }}>

                                <div {...{
                                    className: cx("toggle", {
                                        disabled: !editions.some((e) => isUnselectable(e.editionId))
                                    }),

                                    onClick: () => {
                                        if (selected === true) {
                                            deselect(editions.map((e) => e.editionId).filter((eId) => isSelected(eId) && isUnselectable(eId)));
                                        } else {
                                            select(editions.map((e) => e.editionId).filter((id) => !isSelected(id)));
                                        }
                                    },
                                }}>
                                    {(() => {
                                        const checked = checkMidwaySelect(editions, (e) => isSelected(e.editionId));
                                        const disabled = !editions.some((e) => isUnselectable(e.editionId));
                                        return Checkbox({
                                            checked,
                                            size: "small",
                                            disabled,
                                            xdQaName: "Select series",
                                        });
                                    })()}
                                    <div {...{
                                        className: "series-name",

                                        "xd-qa-type": "label",
                                        "xd-qa-name": "Series Name",
                                    }}>
                                        {series.name} Series
                                    </div>

                                    <div {...{
                                        className: "count",
                                        "xd-qa-type": "label",
                                        "xd-qa-name": "Selected Count",
                                    }}>
                                        {editions.filter((e) => isSelected(e.editionId)).length}{" "}
                                        of{" "}
                                        {editions.length}{" "}
                                        {plural("edition", editions.length)} selected
                                    </div>
                                </div>


                                {cs(
                                    ["pricingModal", (_, next) => PricingTableModal3({
                                        title: program.programPreferences.linkName + " Pricing",
                                        next
                                    })],
                                    ({pricingModal}) => fragments(
                                        <a {...{
                                            onClick: async () => {
                                                const ids = chain(
                                                    editions.map((e) => e.templateId),
                                                    unique,
                                                );
                                                const templateOptionsList = await Promise.all(ids.map((id) => apis.next.getTemplateOptions({front: +id, back: +id})));
                                                pricingModal.show({list: templateOptionsList});
                                            },
                                            "xd-qa-type": "link",
                                            "xd-qa-name": "Pricing Table",
                                        }}>
                                            Pricing Table
                                        </a>
                                    )
                                )}
                            </div>

                            {next(editions)}

                            {cs(
                                ["actions", ({}, next) => next([
                                    autoEnroll.renderSeriesAction({
                                        seriesId: series.id,
                                    }),
                                    autoRenew.renderSeriesAction({
                                        seriesId: series.id,
                                        onSelect: () => autoEnroll.unselect(series.id),
                                    }),
                                ].filter(v => v))],
                                ({actions}) => !!actions.length && (
                                    <div className="actions">
                                        {actions.map((action, i) => cs(
                                            keyed(i),
                                            () => action,
                                        ))}
                                    </div>
                                ),
                            )}
                        </div>
                    )
                ))
            )],
            ["editionsPerRow", (_, next) => DomSizeObserver({
                translate: (s) => (
                    s.width > 751 ? (
                        3
                    ) : s.width > 498 ? (
                        2
                    ) : (
                        1
                    )
                ),
                next,
            })],
            ["expanded", (_, next) => UseState({next})],
            ({editions1, expanded, editionsPerRow}, next) => fragments(
                next(),
                editions1.length > editionsPerRow.value && (
                    <div {...{
                        className: "expanse-indicator",
                        onClick: () => expanded.change(v => !v),
                        "xd-qa-type": "button",
                        "xd-qa-name": !expanded.value ? (
                            `Show All Editions`
                        ) : (
                            `Show Less Editions`
                        ),
                    }}>
                        {!expanded.value ? <>
                            <div className="text">
                                Show All Editions ({editions1.length})
                            </div>
                            <i className="fa fa-chevron-down"/>
                        </> : <>
                            <div className="text">
                                Show Less Editions ({editions1.length})
                            </div>
                            <i className="fa fa-chevron-up"/>
                        </>}
                    </div>
                ),
            ),
            ({editions1, editionsPerRow, expanded}) => (
                <div {...{
                    className: "grid",
                    ref: editionsPerRow.ref,
                    style: {
                        "--editionsPerRow": editionsPerRow.value,
                    } as any,
                }}>
                    {chain(
                        editions1,
                        (_) => sort<any>(_, (e) => e.enrollmentDeadline),
                        (_) => expanded.value ? _ : _.slice(0, editionsPerRow.value),
                        (_) => _.map((edition) => cs(
                            keyed(edition.editionId),
                            ["templateOptions", (_, next) => next(
                                templateOptionsList.all.find((to) => to && to.sku.front === +edition.templateId)
                            )],
                            ({templateOptions}) => EditionCard1({
                                edition,
                                productName: to_getFriendlyName(templateOptions) ?? templateOptions?.product?.name,
                                className: "edition",
                                selected: calValue?.some((edition1) => edition1.editionId === edition.editionId),
                                disabled: !isEnrollable(edition),
                                getStyleTemplate,
                                ...isUnselectable(edition.editionId) && {
                                    onDeselect: () => deselect([edition.editionId]),
                                },
                                onSelect: () => select([edition.editionId]),
                            })
                        )),
                    )}
                </div>
            ),
        ),
    }),
);

